import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="iso-week-calendar"
export default class extends Controller {
  static targets = [
    "startDate",
    "endDate",
    "preferredWeeks",
    "weekCell",
    "selectionSummary",
    "shrinkNotice",
    "shrinkMessage",
    "nextPage",
    "windowLabel",
    "nameInput",
  ];
  static values = {
    preferredWeeks: { type: Number, default: 1 },
    minWeeks: { type: Number, default: 1 },
    maxWeeks: { type: Number, default: 4 },
    pageSize: { type: Number, default: 12 },
    windowOffset: { type: Number, default: 0 },
    weeksPayload: { type: Array, default: [] },
    autoFilled: { type: Boolean, default: true },
    preferredSpanUrl: { type: String, default: "" },
    weeksUrl: { type: String, default: "" },
    editing: { type: Boolean, default: false },
    exceptId: { type: Number, default: 0 },
    suggestedName: { type: String, default: "" },
  };

  connect() {
    this.autoFilled = this.autoFilledValue;
    this.#syncUi();
  }

  disconnect() {
    this.#abortPreferredSpanFetch();
  }

  async previousPage() {
    if (this.windowOffsetValue === 0) {
      if (await this.#prependEarlierPage()) this.#renderWindow();
      return;
    }

    this.windowOffsetValue = Math.max(0, this.windowOffsetValue - this.pageSizeValue);
    this.#renderWindow();
  }

  nextPage() {
    const maxOffset = Math.max(0, this.weeksPayloadValue.length - this.pageSizeValue);
    const nextOffset = Math.min(maxOffset, this.windowOffsetValue + this.pageSizeValue);
    if (nextOffset === this.windowOffsetValue) return;
    this.windowOffsetValue = nextOffset;
    this.#renderWindow();
  }

  preferredWeeksChanged() {
    if (!this.hasPreferredWeeksTarget) return;

    const weeks = this.#clampedPreferredWeeks(this.preferredWeeksTarget.value);
    this.preferredWeeksTarget.value = String(weeks);
    this.preferredWeeksValue = weeks;

    const start = this.startDateTarget.value || null;
    if (start) {
      return this.#applyDefaultFill(start);
    }
  }

  weekClicked(event) {
    const cell = event.currentTarget;
    if (cell.dataset.selectable !== "true") return;

    const monday = cell.dataset.monday;
    const start = this.startDateTarget.value || null;
    const endMonday = this.#endMondayFromHidden();

    if (start && start === endMonday && monday === start) {
      this.#clearSelection();
      return;
    }

    return this.#applyDefaultFill(monday);
  }

  #clampedPreferredWeeks(rawValue) {
    const parsed = parseInt(rawValue, 10);
    const weeks = Number.isNaN(parsed) ? this.minWeeksValue : parsed;
    return Math.min(this.maxWeeksValue, Math.max(this.minWeeksValue, weeks));
  }

  async #applyDefaultFill(startMonday) {
    if (!this.preferredSpanUrlValue) return;

    const url = this.#requestUrl(this.preferredSpanUrlValue);
    url.searchParams.set("start_monday", startMonday);
    url.searchParams.set("preferred_weeks", String(this.preferredWeeksValue));
    url.searchParams.set("editing", this.editingValue ? "true" : "false");

    this.#abortPreferredSpanFetch();
    const abortController = new AbortController();
    this.preferredSpanAbortController = abortController;

    let response;
    try {
      response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        signal: abortController.signal,
      });
    } catch (error) {
      if (error?.name === "AbortError") return;
      throw error;
    }
    if (abortController.signal.aborted) return;
    if (!response.ok) return;

    const span = await response.json();
    if (abortController.signal.aborted) return;
    if (!span?.start_monday || !span?.end_sunday) return;

    this.#setSelection(span.start_monday, span.end_sunday, { autoFilled: true });
    this.#applySuggestedName(span.suggested_name);
    this.dispatch("span-applied", {
      detail: {
        startMonday: span.start_monday,
        endSunday: span.end_sunday,
        reuseAvailable: Boolean(span.reuse_available),
        reuseBalances: span.reuse_balances || {},
      },
    });
  }

  // Loads the page of weeks just before the loaded ones, so users can page back without limit.
  async #prependEarlierPage() {
    if (!this.weeksUrlValue || this.loadingEarlierPage) return false;

    const url = this.#requestUrl(this.weeksUrlValue);
    url.searchParams.set("before", this.weeksPayloadValue[0].monday);

    this.loadingEarlierPage = true;
    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return false;

      const { weeks } = await response.json();
      this.weeksPayloadValue = [...weeks, ...this.weeksPayloadValue];
      this.weekByMonday = null;
      return true;
    } finally {
      this.loadingEarlierPage = false;
    }
  }

  #requestUrl(path) {
    const url = new URL(path, window.location.origin);
    if (this.exceptIdValue > 0) {
      url.searchParams.set("except_id", String(this.exceptIdValue));
    }
    return url;
  }

  #abortPreferredSpanFetch() {
    if (!this.preferredSpanAbortController) return;
    this.preferredSpanAbortController.abort();
    this.preferredSpanAbortController = null;
  }

  #setSelection(startMonday, endSunday, { autoFilled }) {
    this.autoFilled = autoFilled;
    this.startDateTarget.value = startMonday;
    this.endDateTarget.value = endSunday;
    this.startDateTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.#syncUi();
  }

  #clearSelection() {
    this.autoFilled = false;
    this.startDateTarget.value = "";
    this.endDateTarget.value = "";
    this.startDateTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.#syncUi();
    this.#applySuggestedName("");
    this.dispatch("span-cleared");
  }

  #applySuggestedName(name) {
    if (!this.hasNameInputTarget) return;
    if (this.nameInputTarget.value !== this.suggestedNameValue) return;

    this.suggestedNameValue = name || "";
    this.nameInputTarget.value = this.suggestedNameValue;
  }

  #renderWindow() {
    const pageWeeks = this.weeksPayloadValue.slice(
      this.windowOffsetValue,
      this.windowOffsetValue + this.pageSizeValue
    );

    this.weekCellTargets.forEach((cell, index) => {
      const week = pageWeeks[index];
      if (!week) {
        cell.classList.add("hidden");
        return;
      }

      cell.classList.remove("hidden");
      cell.dataset.monday = week.monday;
      cell.dataset.week = String(week.week);
      cell.dataset.occupied = week.occupied ? "true" : "false";
      cell.dataset.past = week.past ? "true" : "false";
      cell.dataset.current = week.current ? "true" : "false";
      cell.dataset.selectable = week.selectable ? "true" : "false";
      cell.disabled = !week.selectable;

      const numberTarget = cell.querySelector('[data-iso-week-calendar-target="weekNumber"]');
      if (numberTarget) {
        numberTarget.textContent = this.#weekNumberText(week.week);
      }

      const currentBadgeTarget = cell.querySelector(
        '[data-iso-week-calendar-target="weekCurrentBadge"]'
      );
      if (currentBadgeTarget) {
        currentBadgeTarget.classList.toggle("hidden", !week.current);
      }

      const rangeTarget = cell.querySelector('[data-iso-week-calendar-target="weekRange"]');
      if (rangeTarget) {
        rangeTarget.textContent = this.#weekRangeText(week.start_short, week.end_short);
      }

      const balancesTarget = cell.querySelector('[data-iso-week-calendar-target="weekBalances"]');
      if (balancesTarget) {
        const balances = Array.isArray(week.balances) ? week.balances : [];
        if (balances.length === 0) {
          balancesTarget.textContent = "";
          balancesTarget.classList.add("hidden");
        } else {
          balancesTarget.classList.remove("hidden");
          const heading = document.createElement("span");
          heading.className = "block font-medium text-base-content/60";
          heading.textContent =
            this.element.dataset.actualBalanceLabel || "Ending inventory";
          const lines = balances.map((balance) => {
            const line = document.createElement("div");
            line.textContent = `${balance.label}: ${balance.weight}`;
            return line;
          });
          balancesTarget.replaceChildren(heading, ...lines);
        }
      }

      const badgeTarget = cell.querySelector('[data-iso-week-calendar-target="weekBadge"]');
      if (badgeTarget) {
        const badgeText = this.#weekBadgeText(week);
        if (badgeText) {
          badgeTarget.textContent = badgeText;
          badgeTarget.classList.remove("hidden");
        } else {
          badgeTarget.textContent = "";
          badgeTarget.classList.add("hidden");
        }
      }
    });

    this.#updateNav();
    this.#syncUi();
  }

  #updateNav() {
    const maxOffset = Math.max(0, this.weeksPayloadValue.length - this.pageSizeValue);
    if (this.hasNextPageTarget) {
      this.nextPageTarget.disabled = this.windowOffsetValue >= maxOffset;
    }
    if (this.hasWindowLabelTarget) {
      const pageWeeks = this.weeksPayloadValue.slice(
        this.windowOffsetValue,
        this.windowOffsetValue + this.pageSizeValue
      );
      const first = pageWeeks[0];
      const last = pageWeeks[pageWeeks.length - 1];
      if (first && last) {
        const template =
          this.element.dataset.windowLabelTemplate ||
          "Weeks %{start_week}–%{end_week} · %{year}";
        this.windowLabelTarget.textContent = template
          .replace("%{start_week}", String(first.week))
          .replace("%{end_week}", String(last.week))
          .replace("%{year}", [...new Set([first.year, last.year])].join("–"))
          .replace("%{start_date}", first.start_short)
          .replace("%{end_date}", last.end_short);
      }
    }
  }

  #syncUi() {
    const start = this.startDateTarget.value || null;
    const endMonday = this.#endMondayFromHidden();
    const weekCount = start && endMonday ? this.#weekCount(start, endMonday) : 0;

    this.weekCellTargets.forEach((cell) => {
      if (cell.classList.contains("hidden")) return;
      const monday = cell.dataset.monday;
      const selected = Boolean(start && endMonday && monday >= start && monday <= endMonday);
      cell.setAttribute("aria-selected", selected ? "true" : "false");
    });

    if (this.hasSelectionSummaryTarget) {
      if (start && endMonday) {
        this.selectionSummaryTarget.textContent = this.#summaryText(start, endMonday, weekCount);
        this.selectionSummaryTarget.classList.remove("hidden");
      } else {
        this.selectionSummaryTarget.textContent = "";
        this.selectionSummaryTarget.classList.add("hidden");
      }
    }

    if (this.hasShrinkNoticeTarget) {
      const showShrink =
        this.autoFilled && weekCount > 0 && weekCount < this.preferredWeeksValue;
      this.shrinkNoticeTarget.classList.toggle("hidden", !showShrink);
      if (showShrink && this.hasShrinkMessageTarget) {
        this.shrinkMessageTarget.textContent = this.#shrinkText(weekCount);
      }
    }
  }

  #summaryText(startMonday, endMonday, weekCount) {
    const template =
      weekCount === 1
        ? this.element.dataset.selectionSummaryOneTemplate ||
          "%{weeks} week · Week %{start_week}: %{start_date} – %{end_date}"
        : this.element.dataset.selectionSummaryTemplate ||
          "%{weeks} weeks · Weeks %{start_week}–%{end_week}: %{start_date} – %{end_date}";
    const startWeek = this.#weekData(startMonday);
    const endWeek = this.#weekData(endMonday);
    const startLabel = startWeek?.start_label || startMonday;
    const endLabel = endWeek?.end_label || this.#addDays(endMonday, 6);
    return template
      .replace("%{weeks}", String(weekCount))
      .replace("%{start_week}", String(startWeek?.week ?? ""))
      .replace("%{end_week}", String(endWeek?.week ?? ""))
      .replace("%{start_date}", startLabel)
      .replace("%{end_date}", endLabel);
  }

  #shrinkText(actualWeeks) {
    const template =
      this.element.dataset.shrinkNoticeTemplate ||
      "Using %{actual_weeks} week(s) instead of %{preferred_weeks} because a later week is taken.";
    return template
      .replace("%{actual_weeks}", String(actualWeeks))
      .replace("%{preferred_weeks}", String(this.preferredWeeksValue));
  }

  #weekBadgeText(week) {
    if (week.past && week.occupied) {
      return this.element.dataset.pastWithCycleLabel || "Past · Cycle created";
    }
    if (week.past) {
      return this.element.dataset.pastLabel || "Past";
    }
    if (week.occupied) {
      return this.element.dataset.takenLabel || "Taken";
    }
    return "";
  }

  #weekNumberText(week) {
    const template = this.element.dataset.weekNumberTemplate || "Week %{week}";
    return template.replace("%{week}", String(week));
  }

  #weekRangeText(startShort, endShort) {
    const template =
      this.element.dataset.weekRangeTemplate || "%{start_date} – %{end_date}";
    return template.replace("%{start_date}", startShort).replace("%{end_date}", endShort);
  }

  #endMondayFromHidden() {
    if (!this.endDateTarget.value) return null;
    return this.#addDays(this.endDateTarget.value, -6);
  }

  #weekData(monday) {
    if (!this.weekByMonday) {
      this.weekByMonday = new Map(
        this.weeksPayloadValue.map((week) => [week.monday, week])
      );
    }
    return this.weekByMonday.get(monday);
  }

  #weekCount(startMonday, endMonday) {
    const start = new Date(`${startMonday}T00:00:00`);
    const end = new Date(`${endMonday}T00:00:00`);
    return Math.round((end - start) / 604800000) + 1;
  }

  #addDays(dateString, days) {
    const date = new Date(`${dateString}T00:00:00`);
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
